import React, { useContext, useEffect, useState } from "react";
import Card from "src/components/Cards/Basic";
import { useOnlineReservations } from "../hooks/useOnlineReservations";
import { dateFormatter, dateTimeFormatter } from "../../../../global";
import {
  faEye as DetailsIcon,
  faPencil as EditIcon,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OnlineReservationDetailsDialog from "../OnlineReservationDetailsDialog";
import { Badge, Col, Form as BTForm, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "../../../../config/axios";
import history from "../../../../global/history";
import SimplePagination from "../../../../components/SimplePagination";
import OrderButtonDropdown from "../../../../components/UiComponents/Buttons/OrderButtonDropdown";
import TableIconButton from "../../../../components/UiComponents/Buttons/TableIconButton";
import {
  keepState,
  modifyState,
} from "../../../../components/UiComponents/Buttons/BackButton/helper";
import { Link } from "react-router-dom";
import FilterResetButton from "../../../../components/UiComponents/Buttons/FilterResetButton";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import useFilter from "../../../../global/useFilter";
import MultiSelectBox from "../../../../components/Forms/MultiSelectBox";
import { showSuccessToast } from "../../../../components/Toasts";
import { DashboardContext } from "../../../../routes/DashboardRoutes";
import { useDebounce } from "use-debounce";
import Table from "../../../../components/Tables/Table";

const columns = [
  { value: "id", name: "#" },
  { value: "parking_slot", name: "Stellplatz" },
  { value: "contact_person", name: "Kontaktperson" },
  { value: "contact_email", name: "E-Mail" },
  { value: "from", name: "Anreise" },
  { value: "to", name: "Abreise" },
  { value: "created_at", name: "Erstellt am" },
  { value: "options", name: "Optionen" },
];

const OnlineReservationsPage = () => {
  const {
    collection: onlineReservations,
    fetchCollection,
    pagination,
    loading,
  } = useOnlineReservations();
  const [selected, setSelected] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const { t } = useTranslation("dashboard");
  const [parkingSlots, setParkingSlots] = useState([]);
  const { settings } = useContext(DashboardContext);
  const [filterShow, setFilterShow] = useState(false);
  const { pathname } = useLocation();
  const { filter, setFilter, resetFilter } = useFilter(pathname, {
    statuses: ["reservation_requested"],
    page: 1,
    order: "created_at_desc",
    parking_slot_id: "",
    hide_past_reservations: true,
    search: "",
  });

  const statuses = filter.statuses.map(
    (status) =>
      ({
        requested: "reservation_requested",
        accepted: "reservation_accepted",
        declined: "reservation_declined",
      }[status] || status)
  );

  const [searchValue] = useDebounce(filter.search, 400);

  const businessSettings = settings?.business;

  const showOnlineReservationDialog = selected !== null;

  useEffect(() => {
    fetchCollection({ ...filter, statuses });
  }, [
    filter.statuses,
    filter.page,
    filter.order,
    filter.parking_slot_id,
    filter.hide_past_reservations,
    searchValue,
  ]);

  useEffect(() => {
    if (businessSettings?.parking_slots_enabled) {
      axios
        .get("/api/v1/parking_slots.json")
        .then(function (response) {
          setParkingSlots(response.data.entries);
        })
        .catch(function (error) {
          console.error(error);
        });
    }
  }, [businessSettings]);

  function renderFilters() {
    if (!filterShow) {
      return <></>;
    }
    return (
      <>
        <Row className="optional-filter-container align-items-center">
          {businessSettings?.parking_slots_enabled && parkingSlots.length > 0 && (
            <Col md={3} xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>Stellplatz</Form.Label>
                <Form.Control
                  as={"select"}
                  className="form-control ui-input-bg"
                  value={filter.parking_slot_id}
                  onChange={(e) => {
                    setFilter({
                      ...filter,
                      parking_slot_id:
                        e.target.value === "null" ? undefined : e.target.value,
                      page: 1,
                    });
                  }}
                >
                  <option value="null">Alle</option>
                  {parkingSlots.map((parkingSlot) => (
                    <option key={parkingSlot.id} value={parkingSlot.id}>
                      {parkingSlot.name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className="text-muted">&nbsp;</Form.Text>
              </Form.Group>
            </Col>
          )}
        </Row>
      </>
    );
  }

  const goToPage = (page) => {
    setFilter((prev) => {
      return { ...prev, page: page };
    });
  };

  function handleReservationUpdated() {
    fetchCollection({ ...filter, statuses });
    setSelected(null);
    showSuccessToast();
  }

  const header = (
    <div className="d-flex flex-wrap w-100 justify-content-between align-items-center">
      <span>Reservierungen</span>
      <FilterResetButton
        resetCallback={() => {
          resetFilter();
        }}
      />
    </div>
  );

  return (
    <>
      <Card title={header}>
        <div className="filter-container">
          <div className={"d-flex w-100 align-items-center flex-wrap"}>
            <div className="mr-3 mt-2 mb-3 mb-sm-0 justify-content-between justify-content-sm-start">
              <OrderButtonDropdown
                selected={filter.order}
                orders={[
                  { key: "created_at_desc", value: "Neueste zuerst" },
                  { key: "created_at_asc", value: "Älteste zuerst" },
                  { key: "from_asc", value: "Anreisedatum aufsteigend" },
                  { key: "from_desc", value: "Anreisedatum absteigend" },
                ]}
                callback={(selected_value) => {
                  setFilter({
                    ...filter,
                    order: selected_value,
                  });
                }}
              />
            </div>
            <div className={"mb-3 mb-sm-0 mr-3"}>
              <MultiSelectBox
                label={"Status"}
                options={[
                  { name: "Angefragt", value: "reservation_requested" },
                  { name: "Akzeptiert", value: "reservation_accepted" },
                  { name: "Abgelehnt", value: "reservation_declined" },
                ]}
                onChange={(statuses) =>
                  setFilter({ ...filter, statuses: statuses })
                }
                selectedValues={statuses}
              />
            </div>
            {businessSettings?.parking_slots_enabled &&
              parkingSlots.length > 0 && (
                <div className={"mr-3 mb-3 mb-sm-0"}>
                  <Form.Group>
                    <Form.Label>Stellplatz</Form.Label>
                    <Form.Control
                      as={"select"}
                      className={`form-control ui-input-bg`}
                      value={filter.parking_slot_id}
                      onChange={(e) => {
                        setFilter({
                          ...filter,
                          parking_slot_id:
                            e.target.value === "null" ? null : e.target.value,
                          page: 1,
                        });
                      }}
                    >
                      <option value="null">Alle</option>
                      {parkingSlots.map((parkingSlot) => (
                        <option key={parkingSlot.id} value={parkingSlot.id}>
                          {parkingSlot.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </div>
              )}
            <Form.Group className="mb-3 flex-grow-1">
              <Form.Label>Nach Reservierungen suchen</Form.Label>
              <div className="position-relative d-flex">
                <div className="position-absolute p-2">
                  <FontAwesomeIcon icon={faSearch} />
                </div>
                <Form.Control
                  placeholder="Nach #Id, Vorname, Nachname, E-Mail, Reservierungscode, Stellplatz suchen"
                  type="text"
                  className="ui-input-bg input-icon-p"
                  defaultValue={searchValue}
                  onChange={(e) =>
                    setFilter({ ...filter, search: e.target.value, page: 1 })
                  }
                />
              </div>
            </Form.Group>
          </div>
          <BTForm.Check
            label="Vergangene Reservierungen ausblenden"
            name="hide_past_reservations"
            type="checkbox"
            value={filter.hide_past_reservations}
            onChange={() =>
              setFilter({
                ...filter,
                hide_past_reservations: !filter.hide_past_reservations,
              })
            }
            checked={filter.hide_past_reservations}
            className={"mr-3 mb-3"}
            id={"hide_past_reservations"}
          />
        </div>
        <div className="ui-table-container">
          <Table
            columns={columns}
            empty={!loading && onlineReservations.length === 0}
          >
            {loading ? (
              <>
                {Array.from({ length: 10 }, (_, i) => (
                  <tr key={i}>
                    <td>
                      <div className={"preview w-100px h-25px"}></div>
                    </td>
                    <td>
                      <div className={"preview w-100px h-25px"}></div>
                    </td>
                    <td>
                      <div className={"preview w-100px h-25px"}></div>
                    </td>
                    <td>
                      <div className={"preview w-100px h-25px"}></div>
                    </td>
                    <td>
                      <div className={"preview w-100px h-25px"}></div>
                    </td>
                    <td>
                      <div className={"preview w-100px h-25px"}></div>
                    </td>
                    <td>
                      <div className={"preview w-100px h-25px"}></div>
                    </td>
                    <td>
                      <div className={"preview w-100px h-25px"}></div>
                    </td>
                  </tr>
                ))}
              </>
            ) : (
              <>
                {onlineReservations.map((onlineReservation) => (
                  <tr
                    key={onlineReservation.id}
                    style={{
                      backgroundColor:
                        onlineReservation.to < new Date() ? "#ffd5cf" : "none",
                    }}
                  >
                    <td>
                      <div>{onlineReservation.id}</div>
                      <Badge
                        pill
                        variant={
                          onlineReservation.state === "reservation_accepted"
                            ? "primary"
                            : onlineReservation.state ===
                              "reservation_requested"
                            ? "secondary"
                            : ["active", "finished"].includes(
                                onlineReservation.state
                              )
                            ? "success"
                            : "warning"
                        }
                        text="light"
                      >
                        {onlineReservation.formatted_status}
                      </Badge>
                      {onlineReservation.reservation_request_expires_at && (
                        <Badge pill variant={"warning"} text="light">
                          Fällig am{" "}
                          {dateFormatter(
                            onlineReservation.reservation_request_expires_at
                          )}
                        </Badge>
                      )}
                    </td>
                    <td>{onlineReservation.parking_slot?.name}</td>
                    <td>{onlineReservation.contact_person}</td>
                    <td>{onlineReservation.contact_email}</td>
                    <td>{dateTimeFormatter(onlineReservation.from)}</td>
                    <td>{dateTimeFormatter(onlineReservation.to)}</td>
                    <td>{dateTimeFormatter(onlineReservation.created_at)}</td>
                    <td>
                      <Link
                        to={{
                          pathname: `/dashboard/stays/${onlineReservation.stay_id}`,
                          state: {
                            ...keepState(history),
                            filter_data: filter,
                          },
                        }}
                      >
                        <TableIconButton
                          icon={DetailsIcon}
                          variant={"primary"}
                        />
                      </Link>
                      {!["active", "finished"].includes(
                        onlineReservation.state
                      ) && (
                        <Link
                          to={{
                            pathname: `/dashboard/stays/${onlineReservation.stay_id}/bookings/${onlineReservation.booking_id}/edit`,
                            state: {
                              ...modifyState(history, pathname),
                              filter_data: filter,
                            },
                          }}
                        >
                          <TableIconButton icon={EditIcon} />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </>
            )}
          </Table>
          <SimplePagination
            pagination={pagination}
            onClick={(page) => goToPage(page)}
          />
        </div>
        {/*<Row className="justify-content-end mr-2 mt-5">*/}
        {/*  <FabPlateau child={(<Button className="float-right p-2 pl-3 pr-3" variant="primary" onClick={() => {*/}
        {/*    history.push("/dashboard/reservations/new");*/}
        {/*  }}>*/}
        {/*    <FontAwesomeIcon icon={faAdd}/> &nbsp;Reservierung erstellen*/}
        {/*  </Button>)}/>*/}
        {/*</Row>*/}
      </Card>

      {showOnlineReservationDialog && (
        <OnlineReservationDetailsDialog
          reservationBookingId={selected.id}
          handleClose={() => setSelected(null)}
          handleSubmit={handleReservationUpdated}
        />
      )}
    </>
  );
};

export default OnlineReservationsPage;
